import React from 'react';
import FormField from '../shared/FormField';
import USStateSelect from '../shared/USStateSelect';
import formDataStorage from '../../utils/formDataStorage';
import { Plus, Trash2, Calendar, CreditCard, AlertTriangle, CheckCircle, ChevronDown } from 'lucide-react';

// 美国旅行历史信息表单
const PreviousUSTravelInfoForm = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('PreviousUSTravelInfoForm useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('previous_US_travel_info');
    console.log('PreviousUSTravelInfoForm useEffect - 已保存数据:', savedData);
    console.log('PreviousUSTravelInfoForm useEffect - 当前props数据:', data);
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('PreviousUSTravelInfoForm useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
    
    // 确保last_visa对象有所有必需的字段
    if (data && data.ever_been_issued_visa === true && data.last_visa) {
      const requiredFields = {
        issued_year: '',
        issued_month: '',
        issued_day: '',
        visa_number: '',
        same_type_visa: false,
        same_country_visa: false,
        ten_printed_visa: false,
        visa_lost: false,
        visa_cancelled: false
      };
      
      let needsUpdate = false;
      const updatedLastVisa = { ...data.last_visa };
      
      Object.keys(requiredFields).forEach(field => {
        if (!(field in updatedLastVisa)) {
          updatedLastVisa[field] = requiredFields[field];
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        console.log('PreviousUSTravelInfoForm useEffect - 初始化缺失的last_visa字段');
        onChange('last_visa', updatedLastVisa);
      }
    }
  }, [data, onChange]);
  // 月份映射
  const MONTHS = [
    { value: '1', label: '01月 (JAN)' },
    { value: '2', label: '02月 (FEB)' },
    { value: '3', label: '03月 (MAR)' },
    { value: '4', label: '04月 (APR)' },
    { value: '5', label: '05月 (MAY)' },
    { value: '6', label: '06月 (JUN)' },
    { value: '7', label: '07月 (JUL)' },
    { value: '8', label: '08月 (AUG)' },
    { value: '9', label: '09月 (SEP)' },
    { value: '10', label: '10月 (OCT)' },
    { value: '11', label: '11月 (NOV)' },
    { value: '12', label: '12月 (DEC)' }
  ];

  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = (year, month) => {
    const maxDays = getDaysInMonth(year, month);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1);
      return { value: day, label: `${day}日` };
    });
  };

  // 验证访问日期是否有效（不能是未来日期，不能早于出生日期）
  const validateVisitDate = (year, month, day) => {
    if (!year || !month || !day) return '';
    
    const visitDate = new Date(year, parseInt(month) - 1, parseInt(day));
    const today = new Date();
    today.setHours(23, 59, 59, 999); // 设置为今天结束时间，允许今天的日期
    
    if (visitDate > today) {
      return '访问日期不能是未来日期';
    }
    
    // 检查是否早于出生日期
    const personal1Data = formDataStorage.loadFormData('personal_1');
    if (personal1Data && personal1Data.date_and_place_of_birth) {
      const birthData = personal1Data.date_and_place_of_birth;
      if (birthData.year && birthData.month && birthData.day) {
        // Personal1Form使用的月份格式是'JAN', 'FEB'等，需要转换为数字
        const MONTH_MAP = {
          'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
          'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
        };
        const birthMonth = MONTH_MAP[birthData.month] || parseInt(birthData.month);
        const birthDate = new Date(birthData.year, birthMonth - 1, parseInt(birthData.day));
        
        if (visitDate < birthDate) {
          return '访问日期不能早于出生日期';
        }
      }
    }
    
    return '';
  };

  // 更新访问信息
  const updateVisitInfo = (index, field, value) => {
    const currentVisitInfo = data.visit_info || [];
    const newVisitInfo = [...currentVisitInfo];
    if (!newVisitInfo[index]) {
      newVisitInfo[index] = { day: '', month: '', year: '', lenth_of_stay: '', lenth_unit: '' };
    }
    
    // 如果修改了年或月，需要检查日期是否有效
    if (field === 'year' || field === 'month') {
      const newData = { ...newVisitInfo[index], [field]: value };
      const maxDays = getDaysInMonth(newData.year, newData.month);
      if (newData.day && parseInt(newData.day) > maxDays) {
        newData.day = String(maxDays);
      }
      newVisitInfo[index] = newData;
    } else {
      newVisitInfo[index] = { ...newVisitInfo[index], [field]: value };
    }
    
    onChange('visit_info', newVisitInfo);
  };

  // 添加访问记录
  const addVisitInfo = () => {
    const currentVisitInfo = data.visit_info || [];
    const newVisitInfo = [...currentVisitInfo, { day: '', month: '', year: '', lenth_of_stay: '', lenth_unit: 'D' }];
    onChange('visit_info', newVisitInfo);
  };

  // 删除访问记录
  const removeVisitInfo = (index) => {
    const currentVisitInfo = data.visit_info || [];
    const newVisitInfo = currentVisitInfo.filter((_, i) => i !== index);
    onChange('visit_info', newVisitInfo);
  };

  // 更新驾照信息
  const updateDriverLicense = (index, field, value) => {
    const currentLicenses = data.driver_license || [];
    const newLicenses = [...currentLicenses];
    if (!newLicenses[index]) {
      newLicenses[index] = { number: '', state: '' };
    }
    newLicenses[index] = { ...newLicenses[index], [field]: value };
    onChange('driver_license', newLicenses);
  };

  // 添加驾照
  const addDriverLicense = () => {
    const currentLicenses = data.driver_license || [];
    const newLicenses = [...currentLicenses, { number: '', state: '' }];
    onChange('driver_license', newLicenses);
  };

  // 删除驾照
  const removeDriverLicense = (index) => {
    const currentLicenses = data.driver_license || [];
    const newLicenses = currentLicenses.filter((_, i) => i !== index);
    onChange('driver_license', newLicenses);
  };

  // 验证签证签发日期是否有效（不能是未来日期，不能早于出生日期）
  const validateVisaDate = (year, month, day) => {
    if (!year || !month || !day) return '';
    
    const visaDate = new Date(year, parseInt(month) - 1, parseInt(day));
    const today = new Date();
    today.setHours(23, 59, 59, 999); // 设置为今天结束时间，允许今天的日期
    
    if (visaDate > today) {
      return '签证签发日期不能是未来日期';
    }
    
    // 检查是否早于出生日期
    const personal1Data = formDataStorage.loadFormData('personal_1');
    if (personal1Data && personal1Data.date_and_place_of_birth) {
      const birthData = personal1Data.date_and_place_of_birth;
      if (birthData.year && birthData.month && birthData.day) {
        // Personal1Form使用的月份格式是'JAN', 'FEB'等，需要转换为数字
        const MONTH_MAP = {
          'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
          'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
        };
        const birthMonth = MONTH_MAP[birthData.month] || parseInt(birthData.month);
        const birthDate = new Date(birthData.year, birthMonth - 1, parseInt(birthData.day));
        
        if (visaDate < birthDate) {
          return '签证签发日期不能早于出生日期';
        }
      }
    }
    
    return '';
  };

  // 验证签证丢失年份是否有效（不能是未来年份，不能早于出生年份）
  const validateVisaLostYear = (year) => {
    if (!year) return '';
    
    const currentYear = new Date().getFullYear();
    
    if (parseInt(year) > currentYear) {
      return '签证丢失年份不能是未来年份';
    }
    
    // 检查是否早于出生年份
    const personal1Data = formDataStorage.loadFormData('personal_1');
    if (personal1Data && personal1Data.date_and_place_of_birth) {
      const birthData = personal1Data.date_and_place_of_birth;
      if (birthData.year && parseInt(year) < parseInt(birthData.year)) {
        return '签证丢失年份不能早于出生年份';
      }
    }
    
    return '';
  };

  // 更新上次签证信息
  const updateLastVisa = (field, value) => {
    const currentLastVisa = data.last_visa || {};
    
    // 如果修改了年或月，需要检查日期是否有效
    if (field === 'issued_year' || field === 'issued_month') {
      const newData = { ...currentLastVisa, [field]: value };
      const maxDays = getDaysInMonth(newData.issued_year, newData.issued_month);
      if (newData.issued_day && parseInt(newData.issued_day) > maxDays) {
        newData.issued_day = String(maxDays);
      }
      onChange('last_visa', newData);
    } else {
      onChange('last_visa', { ...currentLastVisa, [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">🇺🇸</span>
          美国旅行历史
        </h3>

        {/* 是否访问过美国 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Calendar className="w-5 h-5 mr-2" />
              您是否曾经去过美国？
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                选择答案 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                                 <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-all duration-200">
                   <input
                     type="radio"
                     name="have_ever_been_in_us"
                     value="false"
                     checked={(data.have_ever_been_in_us !== undefined ? data.have_ever_been_in_us : false) === false}
                     onChange={(e) => {
                       onChange('have_ever_been_in_us', false);
                       onChange('visit_info', []);
                       onChange('ever_hold_driver_license', false);
                       onChange('driver_license', []);
                     }}
                     className="mr-3 w-5 h-5 text-amber-600 border-gray-300 focus:ring-2 focus:ring-amber-500 focus:ring-opacity-50"
                   />
                   <span className="text-base font-medium text-gray-700">❌ 否 - 我从未去过美国</span>
                 </label>
                 
                 <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-all duration-200">
                   <input
                     type="radio"
                     name="have_ever_been_in_us"
                     value="true"
                     checked={(data.have_ever_been_in_us !== undefined ? data.have_ever_been_in_us : false) === true}
                     onChange={(e) => {
                       onChange('have_ever_been_in_us', true);
                       if (!data.visit_info || data.visit_info.length === 0) {
                         addVisitInfo();
                       }
                     }}
                     className="mr-3 w-5 h-5 text-amber-600 border-gray-300 focus:ring-2 focus:ring-amber-500 focus:ring-opacity-50"
                   />
                   <span className="text-base font-medium text-gray-700">✅ 是 - 我去过美国</span>
                 </label>
              </div>
            </div>
          </div>

          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
            <p className="text-sm text-blue-700">
              <strong>说明：</strong> 包括商务、旅游、学习、工作等任何形式的美国访问经历。如果您从未访问过美国，请选择"否"。
            </p>
          </div>
        </div>

        {/* 访问记录详情 - 只在访问过美国时显示 */}
        {data.have_ever_been_in_us && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">访问记录详情</h4>
              <button
                type="button"
                onClick={addVisitInfo}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
              >
                <Plus className="w-4 h-4 mr-1" />
                添加访问记录
              </button>
            </div>
            <p className="text-xs text-amber-600 mb-4">提示：如果以前去过美国，请真实填写入境时间、离境时间、停留天数，尽量对照旧护照/出入境记录。</p>

            <div className="space-y-4">
              {(() => {
                const visitInfo = data.visit_info || [];
                return visitInfo.map((visit, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-gray-50 to-amber-50 rounded-lg border-l-4 border-amber-400 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 space-y-4">
                        {/* 访问日期 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            访问日期 {index + 1} <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-2">年份 <span className="text-red-500">*</span></label>
                                <input
                                  id={`prev_visit_${index}_year`}
                                  type="number"
                                min="1900"
                                max="2030"
                                placeholder="2022"
                                value={visit.year || ''}
                                onChange={(e) => updateVisitInfo(index, 'year', e.target.value)}
                                className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-center bg-gradient-to-r from-white to-amber-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                              <div className="relative">
                                  <select
                                    id={`prev_visit_${index}_month`}
                                    value={visit.month || ''}
                                  onChange={(e) => updateVisitInfo(index, 'month', e.target.value)}
                                  className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-gradient-to-r from-white to-amber-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                                >
                                  <option value="">选择月份</option>
                                  {MONTHS.map(month => (
                                    <option key={month.value} value={month.value}>
                                      {month.label}
                                    </option>
                                  ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <ChevronDown className="h-4 w-4 text-gray-400" />
                                </div>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                              <div className="relative">
                                  <select
                                    id={`prev_visit_${index}_day`}
                                    value={visit.day || ''}
                                  onChange={(e) => updateVisitInfo(index, 'day', e.target.value)}
                                  disabled={!visit.year || !visit.month}
                                  className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-gradient-to-r from-white to-amber-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                >
                                  <option value="">选择日期</option>
                                  {generateDayOptions(visit.year, visit.month).map(day => (
                                    <option key={day.value} value={day.value}>
                                      {day.label}
                                    </option>
                                  ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <ChevronDown className="h-4 w-4 text-gray-400" />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* 访问日期验证错误提示 */}
                          {(() => {
                            const validationError = validateVisitDate(visit.year, visit.month, visit.day);
                            if (validationError) {
                              return (
                                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                                  <div className="flex items-start">
                                    <div className="w-5 h-5 text-red-600 mt-0.5 mr-2">⚠️</div>
                                    <p className="text-sm text-red-800 font-medium">{validationError}</p>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>

                        {/* 停留时间 */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            label="停留天数"
                            type="number"
                            value={visit.lenth_of_stay || ''}
                            onChange={(e) => updateVisitInfo(index, 'lenth_of_stay', e.target.value)}
                            placeholder="15"
                            required
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              时间单位 <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <select
                                value={visit.lenth_unit || ''}
                                onChange={(e) => updateVisitInfo(index, 'lenth_unit', e.target.value)}
                                className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-gradient-to-r from-white to-amber-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                              >
                                <option value="D">天 (Days)</option>
                                <option value="W">周 (Weeks)</option>
                                <option value="M">月 (Months)</option>
                                <option value="Y">年 (Years)</option>
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 删除按钮 */}
                      {data.visit_info && data.visit_info.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeVisitInfo(index)}
                          className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                          title="删除此访问记录"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* 驾照信息 - 只在访问过美国时显示 */}
        {data.have_ever_been_in_us && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <div className="mb-4">
              <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
                <CreditCard className="w-5 h-5 mr-2" />
                您是否曾经持有美国驾驶执照？
              </h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  选择答案 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                                     <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                     <input
                       type="radio"
                       name="ever_hold_driver_license"
                       value="false"
                       checked={(data.ever_hold_driver_license !== undefined ? data.ever_hold_driver_license : false) === false}
                       onChange={(e) => {
                         onChange('ever_hold_driver_license', false);
                         onChange('driver_license', []);
                       }}
                       className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                     />
                     <span className="text-base font-medium text-gray-700">❌ 否 - 我从未持有过美国驾照</span>
                   </label>
                   
                   <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                     <input
                       type="radio"
                       name="ever_hold_driver_license"
                       value="true"
                       checked={(data.ever_hold_driver_license !== undefined ? data.ever_hold_driver_license : false) === true}
                       onChange={(e) => {
                         onChange('ever_hold_driver_license', true);
                         if (!data.driver_license || data.driver_license.length === 0) {
                           addDriverLicense();
                         }
                       }}
                       className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                     />
                     <span className="text-base font-medium text-gray-700">✅ 是 - 我持有过美国驾照</span>
                   </label>
                </div>
              </div>
              
              {data.ever_hold_driver_license && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={addDriverLicense}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    添加驾照
                  </button>
                </div>
              )}
            </div>

            {data.ever_hold_driver_license && (
              <div className="space-y-4">
                {(() => {
                  const licenses = data.driver_license || [];
                  return licenses.map((license, index) => (
                    <div key={index} className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            id={`driver_${index}_number`}
                            label={`驾照号码 ${index + 1}`}
                            value={license.number || ''}
                            onChange={(e) => updateDriverLicense(index, 'number', e.target.value)}
                            placeholder="114514"
                            required
                          />
                          <USStateSelect
                            id={`driver_${index}_state`}
                            label="签发州"
                            value={license.state || ''}
                            onChange={(value) => updateDriverLicense(index, 'state', value)}
                            required
                          />
                        </div>
                        {data.driver_license && data.driver_license.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDriverLicense(index)}
                            className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                            title="删除此驾照"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}

        {/* 签证历史 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <CheckCircle className="w-5 h-5 mr-2" />
              您是否曾经被签发过美国签证？
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                选择答案 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                                 <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                   <input
                     type="radio"
                     name="ever_been_issued_visa"
                     value="false"
                     checked={(data.ever_been_issued_visa !== undefined ? data.ever_been_issued_visa : false) === false}
                     onChange={(e) => {
                       onChange('ever_been_issued_visa', false);
                       onChange('last_visa', {});
                     }}
                     className="mr-3 w-5 h-5 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                   />
                   <span className="text-base font-medium text-gray-700">❌ 否 - 我从未被签发过美国签证</span>
                 </label>
                 
                 <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                   <input
                     type="radio"
                     name="ever_been_issued_visa"
                     value="true"
                     checked={(data.ever_been_issued_visa !== undefined ? data.ever_been_issued_visa : false) === true}
                     onChange={(e) => {
                       onChange('ever_been_issued_visa', true);
                       // 初始化last_visa对象的所有必需字段
                       if (!data.last_visa) {
                         onChange('last_visa', {
                           issued_year: '',
                           issued_month: '',
                           issued_day: '',
                           visa_number: '',
                           same_type_visa: false,
                           same_country_visa: false,
                           ten_printed_visa: false,
                           visa_lost: false,
                           visa_cancelled: false
                         });
                       }
                     }}
                     className="mr-3 w-5 h-5 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                   />
                   <span className="text-base font-medium text-gray-700">✅ 是 - 我被签发过美国签证</span>
                 </label>
              </div>
            </div>
          </div>

          {data.ever_been_issued_visa && (
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400 space-y-4">
              <h5 className="text-md font-semibold text-gray-700 mb-4">最近一次签证信息</h5>

              {/* 签证签发日期 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  签证签发日期 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">年份 <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      min="1900"
                      max="2030"
                      placeholder="2022"
                      value={(data.last_visa && data.last_visa.issued_year) || ''}
                      onChange={(e) => updateLastVisa('issued_year', e.target.value)}
                      className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-center bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                    <div className="relative">
                      <select
                        value={(data.last_visa && data.last_visa.issued_month) || ''}
                        onChange={(e) => updateLastVisa('issued_month', e.target.value)}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                      >
                        <option value="">选择月份</option>
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                    <div className="relative">
                      <select
                        value={(data.last_visa && data.last_visa.issued_day) || ''}
                        onChange={(e) => updateLastVisa('issued_day', e.target.value)}
                        disabled={!data.last_visa?.issued_year || !data.last_visa?.issued_month}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <option value="">选择日期</option>
                        {generateDayOptions(data.last_visa?.issued_year, data.last_visa?.issued_month).map(day => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* 签证签发日期验证错误提示 */}
                {(() => {
                  const validationError = validateVisaDate(
                    data.last_visa?.issued_year, 
                    data.last_visa?.issued_month, 
                    data.last_visa?.issued_day
                  );
                  if (validationError) {
                    return (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex items-start">
                          <div className="w-5 h-5 text-red-600 mt-0.5 mr-2">⚠️</div>
                          <p className="text-sm text-red-800 font-medium">{validationError}</p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* 签证号码 */}
              <FormField
                label="签证号码"
                value={(data.last_visa && data.last_visa.visa_number) || ''}
                onChange={(e) => {
                  // 只允许有效字符：A-Z, 0-9, #, $, *, %, &, (;), !, @, ^, ?, >, <, (), ., ', ,, -, 空格
                  const validChars = /^[A-Z0-9#$*%&();!@^?><().',-\s]*$/i;
                  if (validChars.test(e.target.value)) {
                    updateLastVisa('visa_number', e.target.value);
                  }
                }}
                placeholder="11451489"
                required
              />

              {/* 签证相关问题 */}
              <div className="space-y-4">
                {/* 与本次申请相同类型 */}
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-3">与本次申请相同类型</label>
                   <div className="flex gap-4">
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="same_type_visa"
                         value="false"
                         checked={(data.last_visa && data.last_visa.same_type_visa !== undefined ? data.last_visa.same_type_visa : false) === false}
                         onChange={(e) => updateLastVisa('same_type_visa', false)}
                         className="mr-2 w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">❌ 否</span>
                     </label>
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="same_type_visa"
                         value="true"
                         checked={(data.last_visa && data.last_visa.same_type_visa !== undefined ? data.last_visa.same_type_visa : false) === true}
                         onChange={(e) => updateLastVisa('same_type_visa', true)}
                         className="mr-2 w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">✅ 是</span>
                     </label>
                   </div>
                </div>

                {/* 在同一国家签发 */}
                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">在同一国家签发</label>
                   <div className="flex gap-4">
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="same_country_visa"
                         value="false"
                         checked={(data.last_visa && data.last_visa.same_country_visa !== undefined ? data.last_visa.same_country_visa : false) === false}
                         onChange={(e) => updateLastVisa('same_country_visa', false)}
                         className="mr-2 w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">❌ 否</span>
                     </label>
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="same_country_visa"
                         value="true"
                         checked={(data.last_visa && data.last_visa.same_country_visa !== undefined ? data.last_visa.same_country_visa : false) === true}
                         onChange={(e) => updateLastVisa('same_country_visa', true)}
                         className="mr-2 w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">✅ 是</span>
                     </label>
                   </div>
                </div>

                {/* 采集过十指指纹 */}
                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">采集过十指指纹</label>
                   <div className="flex gap-4">
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="ten_printed_visa"
                         value="false"
                         checked={(data.last_visa && data.last_visa.ten_printed_visa !== undefined ? data.last_visa.ten_printed_visa : false) === false}
                         onChange={(e) => updateLastVisa('ten_printed_visa', false)}
                         className="mr-2 w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">❌ 否</span>
                     </label>
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="ten_printed_visa"
                         value="true"
                         checked={(data.last_visa && data.last_visa.ten_printed_visa !== undefined ? data.last_visa.ten_printed_visa : false) === true}
                         onChange={(e) => updateLastVisa('ten_printed_visa', true)}
                         className="mr-2 w-4 h-4 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">✅ 是</span>
                     </label>
                   </div>
                </div>

                {/* 签证丢失过 */}
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-3">
                     您的美国签证是否曾经丢失或被盗？
                   </label>
                   <div className="flex gap-4">
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="visa_lost"
                         value="false"
                         checked={(data.last_visa && data.last_visa.visa_lost !== undefined ? data.last_visa.visa_lost : false) === false}
                         onChange={(e) => updateLastVisa('visa_lost', false)}
                         className="mr-2 w-4 h-4 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">❌ 否</span>
                     </label>
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="visa_lost"
                         value="true"
                         checked={(data.last_visa && data.last_visa.visa_lost !== undefined ? data.last_visa.visa_lost : false) === true}
                         onChange={(e) => updateLastVisa('visa_lost', true)}
                         className="mr-2 w-4 h-4 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">✅ 是</span>
                     </label>
                   </div>

                  {/* 如果签证被偷/丢失，显示详细信息字段 */}
                  {(data.last_visa && data.last_visa.visa_lost === true) && (
                    <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            输入签证丢失或被盗的年份
                          </label>
                          <input
                            type="number"
                            min="1900"
                            max="2030"
                            placeholder="2022"
                            value={(data.last_visa && data.last_visa.visa_lost_year) || ''}
                            onChange={(e) => updateLastVisa('visa_lost_year', e.target.value)}
                            className="w-32 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-center"
                            required
                          />
                          
                          {/* 签证丢失年份验证错误提示 */}
                          {(() => {
                            const validationError = validateVisaLostYear(data.last_visa?.visa_lost_year);
                            if (validationError) {
                              return (
                                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                                  <div className="flex items-start">
                                    <div className="w-5 h-5 text-red-600 mt-0.5 mr-2">⚠️</div>
                                    <p className="text-sm text-red-800 font-medium">{validationError}</p>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            解释说明 <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            rows="3"
                            maxLength="4000"
                            placeholder="请详细说明签证丢失或被盗的情况..."
                            value={(data.last_visa && data.last_visa.visa_lost_explanation) || ''}
                            onChange={(e) => updateLastVisa('visa_lost_explanation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 签证被取消过 */}
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-3">签证被取消过</label>
                   <div className="flex gap-4">
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="visa_cancelled"
                         value="false"
                         checked={(data.last_visa && data.last_visa.visa_cancelled !== undefined ? data.last_visa.visa_cancelled : false) === false}
                         onChange={(e) => updateLastVisa('visa_cancelled', false)}
                         className="mr-2 w-4 h-4 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">❌ 否</span>
                     </label>
                     <label className="flex items-center cursor-pointer p-2 border border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                       <input
                         type="radio"
                         name="visa_cancelled"
                         value="true"
                         checked={(data.last_visa && data.last_visa.visa_cancelled !== undefined ? data.last_visa.visa_cancelled : false) === true}
                         onChange={(e) => updateLastVisa('visa_cancelled', true)}
                         className="mr-2 w-4 h-4 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                       />
                       <span className="text-sm font-medium text-gray-700">✅ 是</span>
                     </label>
                   </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 签证拒绝历史 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <AlertTriangle className="w-5 h-5 mr-2" />
              曾经被拒绝过美国签
            </h4>
          </div>

          <div className="space-y-6">
            {/* 签证被拒绝 */}
            <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg border-l-4 border-red-400">
              <div className="mb-4">
                                 <label className="block text-sm font-medium text-gray-700 mb-3">
                   曾经被拒绝过美国签证
                 </label>
                 <div className="flex gap-6">
                   <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                     <input
                       type="radio"
                       name="visa_refused"
                       value="false"
                       checked={(data.visa_refused !== undefined ? data.visa_refused : false) === false}
                       onChange={(e) => {
                         onChange('visa_refused', false);
                         onChange('visa_refused_reason', '');
                       }}
                       className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                     />
                     <span className="text-base font-medium text-gray-700">❌ 否 - 我从未被拒绝过美国签证</span>
                   </label>
                   
                   <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                     <input
                       type="radio"
                       name="visa_refused"
                       value="true"
                       checked={(data.visa_refused !== undefined ? data.visa_refused : false) === true}
                       onChange={(e) => {
                         onChange('visa_refused', true);
                       }}
                       className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                     />
                     <span className="text-base font-medium text-gray-700">⚠️ 是 - 我曾经被拒绝过美国签证</span>
                   </label>
                 </div>
              </div>

              {data.visa_refused && (
                <FormField
                  label="拒绝原因"
                  type="textarea"
                  value={data.visa_refused_reason || ''}
                  onChange={(e) => onChange('visa_refused_reason', e.target.value)}
                  placeholder="请详细说明签证被拒绝的原因..."
                />
              )}
            </div>


          </div>
        </div>

        {/* 移民申请历史 */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">移民申请信息</h4>
            
            <div>
                             <label className="block text-sm font-medium text-gray-700 mb-3">
                 是否曾经有人为我办理过移民申请
               </label>
               <div className="flex gap-6">
                 <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all duration-200">
                   <input
                     type="radio"
                     name="has_petition"
                     value="false"
                     checked={(data.has_petition !== undefined ? data.has_petition : false) === false}
                     onChange={(e) => {
                       onChange('has_petition', false);
                       onChange('petition_reason', '');
                     }}
                     className="mr-3 w-5 h-5 text-purple-600 border-gray-300 focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
                   />
                   <span className="text-base font-medium text-gray-700">❌ 否 - 从未有人为我办理过移民申请</span>
                 </label>
                 
                 <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all duration-200">
                   <input
                     type="radio"
                     name="has_petition"
                     value="true"
                     checked={(data.has_petition !== undefined ? data.has_petition : false) === true}
                     onChange={(e) => {
                       onChange('has_petition', true);
                     }}
                     className="mr-3 w-5 h-5 text-purple-600 border-gray-300 focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
                   />
                   <span className="text-base font-medium text-gray-700">✅ 是 - 曾经有人为我办理过移民申请</span>
                 </label>
               </div>
            </div>
          </div>

          {data.has_petition && (
            <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border-l-4 border-purple-400">
              <FormField
                label="移民申请详情"
                type="textarea"
                value={data.petition_reason || ''}
                onChange={(e) => onChange('petition_reason', e.target.value)}
                placeholder="请详细说明移民申请的相关信息，包括申请人、时间、申请类型等..."
              />
            </div>
          )}
        </div>

        {/* 预览信息 */}
        {(data.have_ever_been_in_us || data.ever_been_issued_visa || data.visa_refused || data.has_petition) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-l-4 border-amber-400 shadow-sm">
            <h4 className="font-semibold text-amber-800 mb-2">信息预览</h4>
            <div className="text-sm text-amber-700 space-y-1">
              <p><strong>是否访问过美国:</strong> {data.have_ever_been_in_us ? '✅ 是' : '❌ 否'}</p>
              
              {data.have_ever_been_in_us && data.visit_info && data.visit_info.length > 0 && (
                <p><strong>访问记录数量:</strong> {data.visit_info.length} 次</p>
              )}
              
              {data.have_ever_been_in_us && (
                <p><strong>是否持有过美国驾照:</strong> {data.ever_hold_driver_license ? '✅ 是' : '❌ 否'}</p>
              )}
              
              {data.ever_hold_driver_license && data.driver_license && data.driver_license.length > 0 && (
                <p><strong>驾照数量:</strong> {data.driver_license.length} 个</p>
              )}
              
              <p><strong>是否被签发过美国签证:</strong> {data.ever_been_issued_visa ? '✅ 是' : '❌ 否'}</p>
              
              {data.ever_been_issued_visa && data.last_visa && data.last_visa.visa_number && (
                <p><strong>最近签证号码:</strong> {data.last_visa.visa_number}</p>
              )}
              
              <p><strong>是否被拒绝过签证:</strong> {data.visa_refused ? '⚠️ 是' : '✅ 否'}</p>
              <p><strong>是否有移民申请:</strong> {data.has_petition ? '✅ 是' : '❌ 否'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviousUSTravelInfoForm;
